import { Scene, WallpaperStyleInfo, rgba, share } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'checkered', label: 'Checkered',
  blurb: 'A checkerboard bent into gentle waves, the wavy checkered aesthetic',
  params: [ share( 'cells', 'Cells', 45 ), share( 'warp', 'Warp', 40 ) ],
};

interface Pt { x: number; y: number; }

/**
 * Checkered: a square lattice anchored at the box's top left, every point pushed by a seeded sum
 * of sines, each cell a closed path of four quadratic curves through its bent edge midpoints so
 * the board ripples instead of kinking. The first tile takes every other cell (the light
 * squares), the rest walk the diagonals in between, so two tiles are the classic board and more
 * a multi tone one. Flat vectors at full size, no gradients, no offscreen work.
 */
export function draw( { ctx, w, h, box, colors, rand, p }: Scene ): void {
  // Cells: 4 to 30 columns across the box, square cells, rows follow
  const cols = 4 + Math.round( p[ 0 ] * 26 );
  const cell = box.w / cols;
  // three seeded waves: the sideways shove follows the row (plus a weaker one along the column),
  // the vertical shove follows the column; 0.6 to 1.8 cycles across the box keeps them gentle
  const f1 = 0.6 + rand() * 1.2, ph1 = rand() * Math.PI * 2;
  const f2 = 0.6 + rand() * 1.2, ph2 = rand() * Math.PI * 2;
  const f3 = 0.6 + rand() * 1.2, ph3 = rand() * Math.PI * 2;
  // amplitude: Warp x 0.45 cell, capped where the waves' shear would fold a cell over its
  // neighbour (the displacement's jacobian kept above 0.4, which only bites at very few columns)
  const k1 = Math.PI * f2 / box.w;
  const k2 = 4 * Math.PI * Math.PI * f1 * f3 / ( box.w * box.h );
  const safe = ( Math.sqrt( k1 * k1 + 2.4 * k2 ) - k1 ) / ( 2 * k2 );
  const amp = Math.min( p[ 1 ] * 0.45 * cell, safe );
  // the box's edges cut through cells, never along a cell boundary: half a cell on the left and
  // right, and the same share of a cell at the top and bottom (the rows rarely divide the height),
  // so a bent boundary never peeks in as a sliver of the neighbour's colour along the canvas edge
  const spill = box.h / cell - Math.floor( box.h / cell );
  const ox = box.x - cell / 2;
  const oy = box.y - ( spill <= 0.5 ? ( 1 - spill ) / 2 : 1 - spill / 2 ) * cell;
  // the lattice reaches one cell past the canvas on every side, enough for the biggest shove,
  // so an ultrawide canvas and the portrait crop stay covered
  const c0 = Math.floor( -ox / cell ) - 1;
  const c1 = Math.ceil( ( w - ox ) / cell ) + 1;
  const r0 = Math.floor( -oy / cell ) - 1;
  const r1 = Math.ceil( ( h - oy ) / cell ) + 1;
  // every corner and edge midpoint once, on a half step grid, so neighbours share their edges exactly
  const gw = ( c1 - c0 ) * 2 + 1;
  const grid: Pt[] = [];
  for ( let j = 0; j <= ( r1 - r0 ) * 2; j++ ) {
    for ( let i = 0; i < gw; i++ ) {
      const c = c0 + i / 2, r = r0 + j / 2;
      const x = ox + c * cell, y = oy + r * cell;
      // the waves run in box fractions, so every resolution bends the same way
      const u = ( x - box.x ) / box.w, v = ( y - box.y ) / box.h;
      grid.push( {
        x: x + amp * ( Math.sin( Math.PI * 2 * f1 * v + ph1 ) + 0.5 * Math.sin( Math.PI * 2 * f2 * u + ph2 ) ),
        y: y + amp * Math.sin( Math.PI * 2 * f3 * u + ph3 ),
      } );
    }
  }
  const at = ( i: number, j: number ) => grid[ j * gw + i ];
  // a quadratic that passes through the bent midpoint m: the control point mirrors the chord's middle over m
  const bend = ( from: Pt, m: Pt, to: Pt ) => ctx.quadraticCurveTo( 2 * m.x - ( from.x + to.x ) / 2, 2 * m.y - ( from.y + to.y ) / 2, to.x, to.y );
  // the first tile on every even diagonal, the rest in order along the odd ones
  const n = colors.length;
  const colorOf = ( c: number, r: number ) => {
    const s = c + r;
    if ( n < 2 || ( ( s % 2 ) + 2 ) % 2 === 0 ) return colors[ 0 ];
    const d = Math.floor( ( s - 1 ) / 2 );
    return colors[ 1 + ( ( d % ( n - 1 ) ) + n - 1 ) % ( n - 1 ) ];
  };
  // a hairline stroke in the cell's own colour hides the antialiasing seam between neighbours
  ctx.lineWidth = Math.max( 0.6, 0.6 * box.w / 1920 );
  ctx.lineJoin = 'round';
  for ( let r = r0; r < r1; r++ ) {
    for ( let c = c0; c < c1; c++ ) {
      const i = ( c - c0 ) * 2, j = ( r - r0 ) * 2;
      const p00 = at( i, j ), p10 = at( i + 2, j ), p11 = at( i + 2, j + 2 ), p01 = at( i, j + 2 );
      const fill = rgba( colorOf( c, r ), 1 );
      ctx.fillStyle = fill;
      ctx.strokeStyle = fill;
      ctx.beginPath();
      ctx.moveTo( p00.x, p00.y );
      bend( p00, at( i + 1, j ), p10 );
      bend( p10, at( i + 2, j + 1 ), p11 );
      bend( p11, at( i + 1, j + 2 ), p01 );
      bend( p01, at( i, j + 1 ), p00 );
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
  }
}
